import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { conversationService } from '../services/conversationService';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { MoreHorizontal, MessageCircle, Copy, Download } from 'lucide-react';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../components/ui/breadcrumb';

const ConversationDetails = () => {
  const { conversationId } = useParams();
  const [conversation, setConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioReady, setAudioReady] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);
  const navigate = useNavigate();

  const fetchAudio = useCallback(async (conversationId) => {
    try {
      setAudioLoading(true);
      setAudioReady(false);
      
      const audioData = await conversationService.getConversationAudio(conversationId);
      const blob = new Blob([audioData], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(blob);
      
      // Clean up previous audio URL to prevent memory leaks
      setAudioBlob((prevBlob) => {
        if (prevBlob) {
          URL.revokeObjectURL(prevBlob);
        }
        return audioUrl;
      });

      if (audioRef.current) {
        // Stop any currently playing audio first
        if (!audioRef.current.paused) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
        
        audioRef.current.src = audioUrl;
        audioRef.current.load();
      }
    } catch (err) {
      setAudioLoading(false);
    }
  }, []);

  const fetchConversationDetails = useCallback(async () => {
    setIsLoading(true);
    try {
      // Make the API call using the conversationId from URL params
      const data = await conversationService.getConversationDetails(conversationId);
      
      // Use the actual API response data
      setConversation(data);

      // If has_audio is true, fetch the audio file from the API
      if (data.has_audio) {
        fetchAudio(conversationId);
      }
    } catch (err) {
      setError("Failed to load conversation details");
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, fetchAudio]);

  useEffect(() => {
    if (conversationId) {
      fetchConversationDetails();
    }
  }, [conversationId, fetchConversationDetails]);

  // Cleanup audio URL when component unmounts
  useEffect(() => {
    return () => {
      if (audioBlob) {
        URL.revokeObjectURL(audioBlob);
      }
    };
  }, [audioBlob]);

  const handlePlayAudio = async () => {
    if (audioRef.current && audioReady && !audioLoading) {
      try {
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        } else {
          // Ensure audio is ready before playing
          if (audioRef.current.readyState >= 2) { // HAVE_CURRENT_DATA
            await audioRef.current.play();
            setIsPlaying(true);
          } else {
            // Audio not ready to play yet
          }
        }
      } catch (error) {
        setIsPlaying(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setAudioReady(true);
      setAudioLoading(false);
    }
  };

  const handleAudioError = (error) => {
    setAudioReady(false);
    setAudioLoading(false);
    setIsPlaying(false);
  };

  // const handleGoBack = () => {
  //   navigate('/conversations');
  // };

  const formatTimestamp = (unixTimestamp) => {
    if (!unixTimestamp) return '';
    const date = new Date(unixTimestamp * 1000);
    return `Today, ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatUTCTimestamp = (value) => {
    // Check if the value is a UTC timestamp
    const utcTimestampRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?([+-]\d{2}:\d{2}|Z)$/;
    
    if (typeof value === 'string' && utcTimestampRegex.test(value)) {
      try {
        const date = new Date(value);
        // Convert to +3GMT (UTC+3)
        const utc3Date = new Date(date.getTime() + (3 * 60 * 60 * 1000));
        
        return utc3Date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
          timeZone: 'UTC'
        }) + ' +3GMT';
      } catch (error) {
        return value; // Return original value if parsing fails
      }
    }
    
    return value; // Return original value if not a timestamp
  };

  const getStatusDisplay = (callSuccessful) => {
    switch (callSuccessful) {
      case true:
      case 'true':
      case 'success':
        return 'Successful';
      case false:
      case 'false':
      case 'failure':
        return 'Failed';
      case 'unknown':
      default:
        return 'Unknown';
    }
  };

  const getStatusClass = (callSuccessful) => {
    switch (callSuccessful) {
      case true:
      case 'true':
      case 'success':
        return 'bg-success-background text-success-foreground';
      case false:
      case 'false':
      case 'failure':
        return 'bg-destructive-background text-destructive';
      case 'unknown':
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-muted/50">
        {/* Header skeleton */}
        <div className="bg-card border-b border-border/60">
          <div className="flex h-16 items-center justify-between px-8 max-w-4xl mx-auto">
            <div className="flex items-center space-x-2">
              <Skeleton className="h-4 w-20" />
              <span>/</span>
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-8 space-y-6">
          {/* Conversation title skeleton */}
          <div className="space-y-1">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
          
          {/* Content area skeleton */}
          <div className="space-y-6">
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <div className="flex space-x-4">
                  <Skeleton className="h-10 w-20" />
                  <Skeleton className="h-10 w-20" />
                </div>
              </CardContent>
            </Card>

            {/* Audio player skeleton */}
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <Skeleton className="h-6 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-16 w-full" />
              </CardContent>
            </Card>

            {/* Metadata skeleton */}
            <Card className="border border-border bg-card shadow-none">
              <CardHeader className="pb-3">
                <Skeleton className="h-6 w-28" />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-5 w-24" />
                </div>
                <div className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-32" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-muted/50">
        <div className="max-w-4xl mx-auto p-8">
          <div className="bg-destructive/10 text-destructive p-6 rounded-lg border border-destructive/20">
            Error loading conversation: {error}
          </div>
        </div>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-muted/50">
        <div className="max-w-4xl mx-auto p-8">
          <div className="bg-yellow-50 text-yellow-600 p-6 rounded-lg border border-yellow-200">
            Conversation not found
          </div>
        </div>
      </div>
    );
  }

  // Extract important data from the conversation object
  const callSuccessful = conversation.analysis?.call_successful;
  const transcriptSummary = conversation.analysis?.transcript_summary || 'No summary available';
  const agentName = conversation.metadata?.agent_name || 'Agent';
  
  // Count successful criteria if available
  const successCriteria = conversation.analysis?.evaluation_criteria_results || {};
  const successfulCriteria = Object.values(successCriteria).filter(criterion => criterion.result === 'success').length;
  const totalCriteria = Object.keys(successCriteria).length;

  return (
    <div className="min-h-screen bg-muted/50">
      {/* Header with enhanced styling */}
      <div className="bg-sidebar border-b border-border/60 sticky top-0 z-10">
        <div className="flex h-16 items-center justify-between px-8">
          {/* Left side - Breadcrumb aligned to left edge */}
          <div className="flex items-center">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    asChild
                    className="text-sm font-medium hover:text-primary cursor-pointer text-muted-foreground"
                  >
                    <button onClick={() => navigate('/conversations')}>
                      Conversations
                    </button>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-muted-foreground" />
                <BreadcrumbItem>
                  <BreadcrumbPage className="text-sm font-medium text-card-foreground">
                    {agentName} Conversation
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          {/* Right side - Action buttons aligned to right edge */}
          <div className="flex items-center gap-2">
            {/* Options dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-card-foreground hover:bg-muted border-0 hover:border-0 focus:border-0 active:border-0 outline-0 focus:outline-0 ring-0 focus:ring-0 focus-visible:ring-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/conversations')}>
                  <MessageCircle className="mr-2 h-4 w-4" />
                  All Conversations
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigator.clipboard.writeText(conversationId)}>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy Conversation ID
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Download className="mr-2 h-4 w-4" />
                  Export Transcript
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-8 space-y-6">
        {/* Conversation Name and ID */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold text-card-foreground">Conversation with {agentName}</h1>
          <p className="text-muted-foreground font-mono text-sm">{conversationId}</p>
        </div>

        {/* Audio player */}
        {conversation.has_audio && (
          <Card className="border border-border bg-card shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg font-semibold text-card-foreground">
                Audio Recording
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative h-32 bg-muted/50 rounded-lg mb-4 overflow-hidden border border-border">
                {/* Hidden audio element */}
                <audio
                  ref={audioRef}
                  src={audioBlob}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onLoadedData={() => {
                    setAudioReady(true);
                    setAudioLoading(false);
                  }}
                  onCanPlay={() => setAudioReady(true)}
                  onWaiting={() => setAudioLoading(true)}
                  onPlaying={() => setAudioLoading(false)}
                  onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(0);
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onError={handleAudioError}
                  onAbort={() => {
                    setIsPlaying(false);
                    setAudioLoading(false);
                  }}
                  preload="metadata"
                />

                {/* Waveform visualization (simplified) */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <svg width="100%" height="100%" viewBox="0 0 800 100" preserveAspectRatio="none">
                    {Array.from({ length: 100 }).map((_, i) => {
                      const height = 10 + Math.random() * 50;
                      const isActive = i * 8 <= (currentTime / (duration || 1)) * 800;
                      return (
                        <rect
                          key={i}
                          x={i * 8}
                          y={50 - height/2}
                          width="3"
                          height={height}
                          fill={isActive ? "#3b82f6" : "#cbd5e1"}
                          rx="1.5"
                        />
                      );
                    })}
                  </svg>
                </div>

                {/* Play button */}
                <div className="absolute left-6 top-1/2 transform -translate-y-1/2">
                  <Button
                    onClick={handlePlayAudio}
                    disabled={!audioReady || audioLoading}
                    className="bg-primary text-primary-foreground rounded-full p-3 h-12 w-12 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {audioLoading ? (
                      <svg className="w-5 h-5 animate-spin" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 4V2A10 10 0 0 0 2 12h2a8 8 0 0 1 8-8Z"/>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        {isPlaying ? (
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        ) : (
                          <path d="M8 5v14l11-7z" />
                        )}
                      </svg>
                    )}
                  </Button>
                </div>

                {/* Time display */}
                <div className="absolute right-6 bottom-4 text-sm font-mono bg-card px-3 py-1 rounded border border-border">
                  {formatDuration(currentTime)} / {formatDuration(duration || conversation.metadata?.call_duration_secs)}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content tabs */}
        <Card className="border border-border bg-card shadow-none">
          <div className="border-b border-border">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-6 font-medium text-sm border-b-2 transition-all ${
                  activeTab === 'overview'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-card-foreground hover:border-slate-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('transcription')}
                className={`py-4 px-6 font-medium text-sm border-b-2 transition-all ${
                  activeTab === 'transcription'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-card-foreground hover:border-slate-300'
                }`}
              >
                Transcription
              </button>
              <button
                onClick={() => setActiveTab('client')}
                className={`py-4 px-6 font-medium text-sm border-b-2 transition-all ${
                  activeTab === 'client'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-card-foreground hover:border-slate-300'
                }`}
              >
                Client data
              </button>
            </nav>
          </div>
          
          <CardContent className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-card-foreground mb-3">
                    Summary
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">{transcriptSummary}</p>
                </div>

                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-semibold text-card-foreground">
                    Call Status
                  </h3>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(callSuccessful)}`}>
                    {getStatusDisplay(callSuccessful)}
                  </span>
                </div>
                
                {totalCriteria > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-card-foreground mb-3">
                      Criteria Evaluation
                    </h3>
                    <div className="mb-4 p-4 bg-muted/50 rounded-lg border border-border">
                      <p className="text-card-foreground font-medium">
                        <span className="text-xl font-bold text-green-600">{successfulCriteria}</span> of <span className="text-xl font-bold text-muted-foreground">{totalCriteria}</span> successful
                      </p>
                    </div>

                    <div className="space-y-3">
                      {Object.entries(successCriteria).map(([criteriaId, criterion], index) => (
                        <div
                          key={criteriaId}
                          className="p-4 bg-muted/50 rounded-lg border border-border"
                        >
                          <div className="flex justify-between items-start">
                            <p className="font-medium capitalize text-card-foreground">{criteriaId.split('_').join(' ')}</p>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              criterion.result === 'success'
                                ? 'bg-success-background text-success-foreground'
                                : 'bg-destructive-background text-destructive'
                            }`}>
                              {criterion.result === 'success' ? '✓ Passed' : '✗ Failed'}
                            </span>
                          </div>
                          {criterion.rationale && (
                            <p className="text-sm text-muted-foreground mt-2">{criterion.rationale}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'transcription' && (
              <div>
                <h3 className="text-lg font-semibold text-card-foreground mb-4">
                  Full Transcription
                </h3>
                {conversation.transcript && conversation.transcript.length > 0 ? (
                  <div className="space-y-4">
                    {conversation.transcript.map((entry, index) => (
                      entry && entry.message && (
                        <div
                          key={index}
                          className={`flex ${entry.role === 'user' ? 'justify-start' : 'justify-end'}`}
                        >
                          <div className={`max-w-lg rounded-lg p-4 border ${
                            entry.role === 'user'
                              ? 'bg-muted/50 text-card-foreground border-border'
                              : 'bg-primary/10 text-card-foreground border-primary/20'
                          }`}>
                            <div className="text-xs mb-2 text-muted-foreground">
                              {entry.role === 'user' ? 'User' : agentName} • {formatDuration(entry.time_in_call_secs)}
                            </div>
                            <p className="leading-relaxed">{entry.message}</p>
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No transcript available for this conversation.</p>
                )}
              </div>
            )}
            
            {activeTab === 'client' && (
              <div>
                <h3 className="text-base font-semibold text-card-foreground mb-4">
                  Client Information
                </h3>
                {conversation.conversation_initiation_client_data?.dynamic_variables ? (
                  <div className="space-y-2">
                    {Object.entries(conversation.conversation_initiation_client_data.dynamic_variables).map(([key, value], index) => (
                      <div
                        key={key}
                        className="flex justify-between py-2"
                      >
                        <span className="text-sm font-medium capitalize text-card-foreground">{key.replace('system__', '').replace(/_/g, ' ')}</span>
                        <span className="text-sm text-muted-foreground">{formatUTCTimestamp(value) || '-'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No client data available for this conversation.</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metadata - Only show on overview tab */}
        {activeTab === 'overview' && (
          <Card className="border border-border bg-card shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-card-foreground">
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between py-2">
                  <span className="text-sm text-muted-foreground font-medium">Date</span>
                  <span className="text-sm font-medium text-card-foreground">{formatTimestamp(conversation.metadata?.start_time_unix_secs)}</span>
                </div>

                <div className="flex justify-between py-2">
                  <span className="text-sm text-muted-foreground font-medium">Connection duration</span>
                  <span className="text-sm font-medium text-card-foreground">{formatDuration(conversation.metadata?.call_duration_secs)}</span>
                </div>

                <div className="flex justify-between py-2">
                  <div>
                    <span className="text-sm text-muted-foreground font-medium">Cost (credits)</span>
                    <p className="text-xs text-muted-foreground mt-1">Development discount applied</p>
                  </div>
                  <span className="text-sm font-medium text-card-foreground">{conversation.metadata?.cost || '-'}</span>
                </div>

                <div className="flex justify-between py-2">
                  <span className="text-sm text-muted-foreground font-medium">LLM Price</span>
                  <span className="text-sm font-medium text-card-foreground">${conversation.metadata?.charging?.llm_price?.toFixed(6) || '0.0000'}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ConversationDetails; 